import {Graphic} from './Graphic';
import {getStore} from '../store';
import {getCollection} from '@/lib/filters/filters';
import {MapService} from '@/services/MapService';
import {LegendGroup} from '@/lib/classes/Legend/LegendGroup';
import {LegendItem} from '@/lib/classes/Legend/LegendItem';
import {LayerOption} from './MapLayer';

class TaxParcelGraphic extends Graphic {
  constructor() {
    super();
  }

  async setMap(map: google.maps.Map | null) {
    if (map) {
      // Register the retry listener before the initial load so a failed load self-heals on the next idle.
      if (!MapService.hasListener('taxParcelIdle')) {
        MapService.addMapListener('taxParcelIdle', 'idle', async () => await getStore().dispatch('loadOverlays', {targetTaxFeatures: true}));
      }
      // Always dispatch: tax overlay objects may already be in the collection (e.g. via the Overlays
      // menu) without their features loaded. The loadOverlays lock keeps redundant calls cheap.
      await getStore().dispatch('loadOverlays', {targetTaxFeatures: true});
      // Read after the load — a pre-load snapshot would miss the overlays the load just added.
      const taxOverlays = getCollection('overlays').getSource().filter((overlay) => overlay.isTaxOverlay);
      taxOverlays.forEach((overlay) => overlay.setRender(true));
    } else {
      const taxOverlays = getCollection('overlays').getSource().filter((overlay) => overlay.isTaxOverlay);
      taxOverlays.forEach((overlay) => overlay.setRender(false));
      MapService.removeMapListener('taxParcelIdle');
    }
  }

  /**
   * Creates legend groups based on the subLayers provided.
   * @param subLayers An array of subLayers that contain legend groups.
   * @return An array of LegendGroup objects created from the subLayers.
   */
  createLegendGroups(subLayers: LayerOption['subLayers']) {
    const legendGroups = [];
    subLayers.forEach((subLayer) => {
      if (subLayer.legendGroups) {
        subLayer.legendGroups.forEach((group) => {
          const legendSymbols = [];
          group.legendSymbols.forEach((symbol) => {
            legendSymbols.push(new LegendItem({
              label: symbol.label,
              fillColor: symbol.fillColor,
              strokeColor: symbol.strokeColor,
              visible: symbol.visible ?? true,
              matcherFn: () => true,
            }));
          });
          legendGroups.push(new LegendGroup({
            label: group.label,
            legendSymbols: legendSymbols,
            legendToggleEnabled: subLayer.legendToggleEnabled ?? true,
          }));
        });
      }
    });
    return legendGroups;
  }

  /**
   * Updates the legend filtering for this graphic.
   * @param legendGroups An array of LegendGroup objects to update the legend groups for this graphic.
   */
  updateLegendFiltering(legendGroups: LegendGroup[]) {
    const legendGroup = legendGroups.find((group) => group.label === 'Labels');
    const legendItem = legendGroup.getItemByLabel('Labels');
    const labelFilterFeatureTypes = ['png', 'polyline', 'label'];
    getCollection('taxFeatures').addFilter('taxParcelLayerFilter', (f) => {
      if (labelFilterFeatureTypes.includes(f.featureType)) {
        return legendItem.matches(f);
      }
      return true;
    });
  }
}

export {
  TaxParcelGraphic,
};
